import React, {useContext, useState, useEffect, useRef} from 'react';

import {AppContext} from '../../../../../AppModule/Context';

import CharactersLimitComponent from './components/charactersLimitComponent/charactersLimitComponent';
import ErrorComponent from './components/errorComponent/errorComponent';
import InputTitleComponent from './components/inputTitleComponent/inputTitleComponent';
import PhoneNumberInput from '../../phoneNumberInput';
import RequiredDotComponent from './components/requiredDotComponent/requiredDotComponent';
import SubTitleComponentV2 from '../subTitleComponentV2/subTitleComponentV2';
import UpgradeComponent from '../upgradeComponent/upgradeComponent';
import EyeIcon from '../../../../../assets/svgComponents/LeftNavigationIcons/EyeIcon';
import utilsService from '../../../../../services/utilsService';

import VIDABOO_CONSTANTS from '../../../../../config/consts';

import './inputComponentV2.css';

const TRIPLE_CLICK_RESET_DURATION = 300;

const InputComponentV2 = ({
  id = '',
  disabled = false,
  disablePaste = false,
  isRequired = false,
  hideCharacterCount = false,
  needEventObject = false,
  noWaitOnInput = false,
  showCustomError = false, //this can be used if user wants to show some error other than handled using validationPattern
  showPasswordToUser = false,
  billingPlanFeature = null,
  defaultValue = '',
  errorMessage = '',
  inputType = 'text', //inputType : text, number, password, phoneNumber, textarea
  LeftComponent = null,
  maxLength = 200, // will not be considered if inputType is number
  maxValue = 100, //will be consider only if inputType is number
  minValue = 0, //will be consider only if inputType is number
  pillType = null,
  placeholder = '',
  RightComponent = null,
  subTitleTxt = '',
  titleTxt = '',
  toolTipPosition = 'right',
  toolTipTxt = '',
  validationPattern = '',
  handleChangesImmediately = () => {},
  onBlur = () => {},
  onChange = () => {},
  onFocus = () => {},
  onInvalidInput = () => {},
  onKeyDown = () => {},
  onUpgrade = null,
}) => {
  const context = useContext(AppContext);

  const clickCount = useRef(0);
  const [typingTimeoutId, setTypingTimeoutId] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [charactersLeft, setCharactersLeft] = useState(
    maxLength - (defaultValue ? defaultValue.length : 0),
  );
  const [isFocused, setIsFocused] = useState(false);
  const [showActualPassword, setShowActualPassword] = useState(false);
  const [tripleClickTimeoutId, setTripleClickTimeoutId] = useState(null);

  useEffect(() => {
    doWhenDefaultValueChanges();
    return () => {
      if (typingTimeoutId) clearTimeout(typingTimeoutId);
      if (tripleClickTimeoutId) clearTimeout(tripleClickTimeoutId);
    };
  }, [defaultValue]);

  useEffect(() => {
    doWhenMaxLengthChanges();
  }, [maxLength]);

  const doWhenDefaultValueChanges = () => {
    if (inputType !== 'number') {
      let value = defaultValue?.trimStart();
      let charactersLeft = maxLength - value?.length;
      setCharactersLeft(charactersLeft);
      if (charactersLeft < 0) {
        setCharactersLeft(0);
        value = value.slice(0, maxLength);
      }
      setValue(value);
    } else {
      if (defaultValue) {
        let value = Number(defaultValue);
        if (value < minValue) {
          setValue(minValue);
        } else if (value > maxValue) {
          setValue(maxValue);
        } else {
          setValue(value);
        }
      }
    }
  };

  const doWhenMaxLengthChanges = () => {
    if (inputType !== 'number') {
      let value = defaultValue?.trimStart();
      let charactersLeft = maxLength - value?.length;
      setCharactersLeft(charactersLeft);
      if (charactersLeft < 0) {
        setCharactersLeft(0);
        value = value.slice(0, maxLength);
      }
      setValue(value);
    }
  };

  const validate = (value, nameRegex) => {
    if (!nameRegex) {
      return true;
    }
    if (nameRegex.test(value)) {
      return true;
    } else {
      return false;
    }
  };

  const handleChange = (event) => {
    if (needEventObject) {
      onChange(event);
      return;
    }
    let id = event.target.id;
    let value = event.target.value.trimStart();
    if (inputType === 'number') {
      if (value < 0 || value === '') {
        value = 0;
      } else {
        value = Number(value);
      }
    }

    if (inputType !== 'number') {
      let charactersLeft = maxLength - value?.length;
      setCharactersLeft(charactersLeft);
      if (charactersLeft < 0) {
        setCharactersLeft(0);
        value = value.slice(0, maxLength);
      } else {
        setHasError(false);
      }
    } else {
      if (value < minValue) {
        value = minValue;
      } else if (value > maxValue) {
        value = maxValue;
      }
    }

    setValue(value);

    if (typingTimeoutId) window.clearTimeout(typingTimeoutId);
    const timeoutMils = noWaitOnInput ? 0 : 1500;
    if (handleChangesImmediately) {
      // in case we want the validation to happen after debounce time but would like to also handle changes immediately in parent
      handleChangesImmediately(id, value);
    }
    const timeoutId = setTimeout(() => {
      if (validate(value, validationPattern)) {
        onChange(id, value);
      } else {
        onInvalidInput(id, value);
        setHasError(true);
      }
    }, timeoutMils);
    setTypingTimeoutId(timeoutId);
  };

  const handleBlur = (event) => {
    if (needEventObject) {
      onBlur(event);
    } else {
      onBlur(id, value);
    }
  };

  const handleTripleClick = () => {
    if (tripleClickTimeoutId) {
      clearTimeout(tripleClickTimeoutId);
    }
    clickCount.current = clickCount.current + 1;
    if (clickCount.current === 3) {
      utilsService.copyTextToClipboard(value);
      const toastObj = utilsService.getToastObj({toastTitle: 'Copied'});
      context.addToastObj(toastObj);
    }
    const timeoutId = setTimeout(() => {
      clickCount.current = 0;
    }, TRIPLE_CLICK_RESET_DURATION);
    setTripleClickTimeoutId(timeoutId);
  };

  const onPaste = (event) => {
    if (disablePaste) {
      event.preventDefault();
      const toastObj = utilsService.getToastObj({
        toastTitle: 'Pasting text is not allowed',
        toastType: VIDABOO_CONSTANTS.TOAST_COMPONENT_TYPES.FAILURE,
      });
      context.addToastObj(toastObj);
    }
  };

  return (
    <div className="et-v2-input-component-container">
      <InputTitleComponent
        pillType={pillType}
        toolTipPosition={toolTipPosition}
        titleTxt={titleTxt}
        toolTipTxt={toolTipTxt}
        billingPlanFeature={billingPlanFeature}
      />
      {subTitleTxt && (
        <div className="et-v2-ic-subtitle-txt">
          <SubTitleComponentV2 subTitleTxt={subTitleTxt} />
        </div>
      )}

      <div className="et-v2-input-component-wrap">
        <div className="et-v2-input-upgrade-component-wrap">
          <UpgradeComponent
            billingPlanFeature={billingPlanFeature}
            onUpgrade={onUpgrade}
          />
        </div>
        {isRequired && <RequiredDotComponent />}
        <div onClick={handleTripleClick}>
          {inputType === 'phoneNumber' ? (
            <div className="et-v2-input-phone-number-component-wrap">
              <PhoneNumberInput
                id={id}
                disabled={disabled}
                placeholder={placeholder}
                value={value}
                variant={'plain'}
                onChange={(e) =>
                  handleChange({
                    target: {
                      id: id,
                      value: e,
                    },
                  })
                }
              />
            </div>
          ) : inputType === 'textarea' ? (
            <textarea
              className={`et-v2-input-text-area ${
                showCustomError || hasError
                  ? 'et-v2-input-text-area-errorneous'
                  : ''
              }  ${isRequired ? 'et-v2-input-text-area-required' : ''}`}
              id={id}
              disabled={disabled}
              placeholder={placeholder}
              value={value}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={(e) => {
                setIsFocused(true);
                onFocus(e);
              }}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
            />
          ) : (
            <div className="et-n-input-left-comp-and-input-wrap">
              {LeftComponent && (
                <div className="et-n-input-left-component">{LeftComponent}</div>
              )}
              <input
                id={id}
                className={`et-v2-input-component ${
                  titleTxt || subTitleTxt
                    ? 'et-v2-input-component-with-margin'
                    : ''
                }  ${!isRequired ? 'et-v2-input-component-not-required' : ''} ${
                  RightComponent ? 'et-v2-input-component-with-right-comp' : ''
                } ${
                  LeftComponent ? 'et-v2-input-component-with-left-comp' : ''
                }  ${disabled ? 'et-v2-input-component-disabled' : ''} ${
                  showCustomError || hasError
                    ? 'et-v2-input-component-errorneous'
                    : ''
                }`}
                disabled={disabled}
                placeholder={placeholder}
                type={showActualPassword ? 'text' : inputType}
                value={value}
                onBlur={handleBlur}
                onChange={handleChange}
                onFocus={(e) => {
                  setIsFocused(true);
                  onFocus(e);
                }}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
              />
            </div>
          )}
        </div>
        {inputType === 'password' && showPasswordToUser && value.length > 0 && (
          <div
            className={`et-n-input-show-password-icon ${
              disabled ? 'et-n-input-show-password-icon-disabled' : ''
            } ${showActualPassword ? 'et-n-input-show-password-icon-selected' : ''}`}
            onClick={() => setShowActualPassword(!showActualPassword)}>
            <EyeIcon />
          </div>
        )}
        {RightComponent && (
          <div className="et-n-input-right-component">{RightComponent}</div>
        )}
      </div>
      {(errorMessage || !hideCharacterCount) &&
        (showCustomError || hasError || (isFocused && !hideCharacterCount)) && (
          <div className="et-v2-input-error-msg-characters-left-wrap">
            {errorMessage && (showCustomError || hasError) && (
              <ErrorComponent errorMessage={errorMessage} />
            )}
            {isFocused && !hideCharacterCount && inputType !== 'number' && (
              <CharactersLimitComponent charactersLeft={charactersLeft} />
            )}
          </div>
        )}
    </div>
  );
};

export default InputComponentV2;
